import React from 'react';
const { useState, useEffect, useRef } = React;
import { DIMENSIONS, t } from '../../diagnosticConfig.js';
import { SD_MODULES, SD_RECOMMENDED_MODULES, SD_NOTE_MAX, SD_TARGET_LEVEL } from '../../systemDesignConfig.js';
import { adminCall, cardSt, labelSt, mutedSt, badgeSt, warnSt, inputSt, textareaSt, tableSt, thSt, tdSt, fmtDate } from './shared.jsx';
import { checkWarnings, StatusBadge } from './AiPipeline.jsx';
import ClientContextCard from './ClientContext.jsx';
import MethodUsedNote, { useMethodTitles, MethodLibraryRecord } from '../MethodUsedNote.jsx';

// System Design (Fase 7): enabled with P4 approved. Ale confirms the modules
// (suggested from P4), enters the data the code needs to calculate (goal, CRM
// stages, contact rate, notes), generates each module (the code calculates, the
// AI writes), edits it with versions and approves it. The playbook is assembled
// by code from the approved modules, published with a link and exported as
// .docx. The final measurement scores again only the worked dimensions, with the
// same rubric; Ale validates and sees the before and after.

const POLL_MS = 4000;
const GATES = {
  p4_not_approved: 'Se habilita con P4 aprobado (pestaña IA).',
  modules_not_confirmed: 'Primero confirma los módulos.',
  module_not_selected: 'Este módulo no está entre los elegidos.',
  no_approved_modules: 'Hace falta al menos un módulo aprobado.',
  rubric_changed: 'La rúbrica cambió desde que se aprobó P4: la medición final no se puede hacer con otra versión.',
  ai_disabled: 'La IA solo corre en diagnósticos de prueba hasta que DIAGNOSTIC_AI_REAL_CLIENTS sea "true".',
  running: 'Ya está corriendo.',
  published: 'Tiene un link publicado: revócalo antes de volver a armarlo.',
  crm_reimport_needed: 'Vuelve a importar el CRM para traer las oportunidades abiertas (pestaña CRM, el mismo export).',
};

/** "300.000", "300000", "1.250.000,50" → a number; '' → null. Spanish thousands and decimals. */
export function parseAmountEs(text) {
  const t = String(text ?? '').replace(/\s|\$|USD|EUR/gi, '');
  if (!t) return null;
  const n = /,/.test(t) ? Number(t.replace(/\./g, '').replace(',', '.')) : /^\d{1,3}(\.\d{3})+$/.test(t) ? Number(t.replace(/\./g, '')) : Number(t);
  return Number.isFinite(n) ? n : NaN;
}
const fmtAmount = (n) => (typeof n === 'number' ? n.toLocaleString('es-ES') : '');
const WHY = { base: 'base (nivel 1-2)', priority: 'prioridad de P4', lowest: 'nivel más bajo' };
const dimension = (key) => DIMENSIONS.find(d => d.key === key);
const dimLabel = (key) => { const d = dimension(key); return d ? `${d.number}. ${t(d.label)}` : key; };
const linkFor = (token) => `${window.location.origin}/entregable?token=${token}`;
const anyRunning = (s) => !!s && [...s.modules, s.score].some(m => m.current?.status === 'running');

async function copy(text) {
  try { await navigator.clipboard.writeText(text); window.__oikeToast?.('Link copiado', 'success'); }
  catch { window.prompt('Copia el link:', text); }
}
function download(fileName, base64) {
  const bytes = Uint8Array.from(atob(base64), c => c.charCodeAt(0));
  const url = URL.createObjectURL(new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' }));
  const a = document.createElement('a');
  a.href = url; a.download = fileName; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}
function openHtml(html) {
  const win = window.open('', '_blank');
  if (!win) { window.alert('Pop-up bloqueado: permite pop-ups para esta página e intenta de nuevo.'); return; }
  win.document.write(html); win.document.close();
}

export default function SystemDesign({ diagnosticId, run, busy }) {
  const [state, setState] = useState(null);
  const [output, setOutput] = useState(null);
  // The data for calculating has changes that are not saved: nothing is generated until they are
  const [inputsDirty, setInputsDirty] = useState(false);
  const timer = useRef(null);
  const titles = useMethodTitles();

  const load = () => run(async () => setState(await adminCall({ action: 'sdState', diagnosticId })));
  useEffect(() => { load(); return () => clearTimeout(timer.current); }, [diagnosticId]);
  useEffect(() => {
    clearTimeout(timer.current);
    if (!anyRunning(state)) return;
    timer.current = setTimeout(async () => {
      try { setState(await adminCall({ action: 'sdState', diagnosticId })); } catch { setState(s => ({ ...s })); }
    }, POLL_MS);
  }, [state]);

  const act = (body) => run(async () => setState(await adminCall({ diagnosticId, ...body })));
  const open = (outputId) => run(async () => setOutput(await adminCall({ action: 'getOutput', outputId })));
  const onOutput = (body) => run(async () => {
    const r = await adminCall(body);
    if (r?.id) setOutput(r); else if (output) setOutput(await adminCall({ action: 'getOutput', outputId: output.id }));
    setState(r?.modules ? r : await adminCall({ action: 'sdState', diagnosticId }));
  });

  if (!state) return null;
  const d = state.diagnostic;
  if (!state.p4Approved) {
    return <div style={cardSt}><div style={labelSt}>System Design</div><div style={mutedSt}>Se habilita cuando P4 (las 9 dimensiones) está aprobado en la pestaña IA.</div></div>;
  }

  return (
    <div>
      {d.isTest && <div style={{ ...warnSt, fontSize: 13 }}>🧪 Diagnóstico de prueba: el playbook publicado y el .docx llevan la marca "Ejemplo con datos ficticios".</div>}
      {d.closed && <div style={{ ...mutedSt, marginBottom: 10 }}>🔒 El diagnóstico está cerrado; System Design sigue funcionando con los datos que el cierre conserva (los registros del role-play ya no están).</div>}
      {!state.ai.enabled && <div style={{ ...warnSt, fontSize: 13 }}>🔒 La IA solo corre en diagnósticos de prueba hasta que DIAGNOSTIC_AI_REAL_CLIENTS sea "true". El playbook se puede armar igual.</div>}

      <ClientContextCard diagnosticId={diagnosticId} run={run} busy={busy} />
      <ModulesCard state={state} busy={busy} act={act} />
      {state.crm.outdated && (
        <div style={{ ...warnSt, fontSize: 13 }}>
          ⚠️ {GATES.crm_reimport_needed} El CRM se importó antes de System Design: al importarlo de nuevo se calculan las 3 oportunidades abiertas más grandes, el canal de cada persona y el valor del pipeline.
          Hasta entonces, Pipeline, Ritual y métricas y Cuentas no se generan.
        </div>
      )}
      {state.settings.confirmedAt && <InputsCard state={state} busy={busy} run={run} onSaved={setState} onDirty={setInputsDirty} />}

      {state.settings.confirmedAt && (
        <div style={cardSt}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
            <div style={labelSt}>Módulos</div>
            <span style={mutedSt}>Modelo: {state.ai.model} · <button className="action-btn" style={{ fontSize: 11, padding: '2px 8px' }} onClick={load} disabled={busy}>↻ Actualizar</button></span>
          </div>
          {state.modules.filter(m => m.selected).map(m => (
            <ModuleRow key={m.key} m={m} busy={busy} titles={titles} selected={output?.step === m.step} blocked={inputsDirty}
              onRun={() => act({ action: 'sdRun', step: m.step })} onOpen={open} />
          ))}
        </div>
      )}

      {state.settings.confirmedAt && <PlaybookCard state={state} busy={busy} act={act} open={open} />}
      {state.settings.confirmedAt && <ScoreCard state={state} busy={busy} act={act} open={open} />}

      {output && <OutputDetail key={`${output.id}:${output.status}:${output.publication?.token || ''}:${output.publication?.revokedAt || ''}`}
        output={output} busy={busy} onAct={onOutput} open={open} run={run} titles={titles} diagnosticId={diagnosticId} state={state} onClose={() => setOutput(null)} />}
    </div>
  );
}

function ModulesCard({ state, busy, act }) {
  const sd = state.settings;
  const suggested = Object.fromEntries(state.suggestion.map(s => [s.key, s.why]));
  const [picked, setPicked] = useState(sd.modules.length ? sd.modules : state.suggestion.map(s => s.key));
  const toggle = (k) => setPicked(p => (p.includes(k) ? p.filter(x => x !== k) : [...p, k]));
  const changed = picked.slice().sort().join() !== sd.modules.slice().sort().join();
  const n = picked.length;
  return (
    <div style={cardSt}>
      <div style={{ ...labelSt, marginBottom: 6 }}>Módulos del System Design</div>
      <div style={{ ...mutedSt, marginBottom: 10 }}>
        Preseleccionados según P4: ICP y Proceso si su dimensión está en nivel 1-2, después las prioridades de P4 que cubre System Design y, hasta 4, las dimensiones más bajas.
        Se trabajan {SD_RECOMMENDED_MODULES.min} o {SD_RECOMMENDED_MODULES.max}. Confírmalos para poder generar.
      </div>
      <div style={{ display: 'grid', gap: 6 }}>
        {SD_MODULES.map(m => (
          <label key={m.key} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, flexWrap: 'wrap' }}>
            <input type="checkbox" checked={picked.includes(m.key)} onChange={() => toggle(m.key)} disabled={busy} />
            <b>{m.number}. {m.label}</b> <span style={mutedSt}>{m.title} · {dimLabel(m.dimension)}: nivel {state.levels?.[m.dimension] ?? '—'}</span>
            {suggested[m.key] && <span style={badgeSt(true)}>sugerido: {WHY[suggested[m.key]]}</span>}
          </label>
        ))}
      </div>
      {(n < SD_RECOMMENDED_MODULES.min || n > SD_RECOMMENDED_MODULES.max) && n > 0 && <div style={{ ...mutedSt, color: '#f59e0b', marginTop: 8 }}>Elegiste {n} módulos; la metodología trabaja {SD_RECOMMENDED_MODULES.min} o {SD_RECOMMENDED_MODULES.max}.</div>}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 10, flexWrap: 'wrap' }}>
        <button className="action-btn btn-primary" disabled={busy || !n || (!changed && !!sd.confirmedAt)} onClick={() => act({ action: 'saveSystemDesign', modules: picked, confirm: true })}>✓ Confirmar módulos</button>
        {sd.confirmedAt && <span style={mutedSt}>Confirmados por {sd.confirmedBy || '—'} el {fmtDate(sd.confirmedAt, true)}{changed ? ' · hay cambios sin confirmar' : ''}</span>}
      </div>
    </div>
  );
}

/** What the code needs to calculate: the goal, the CRM stages of meeting and opportunity, the contact rate and Ale's notes. */
function InputsCard({ state, busy, run, onSaved, onDirty }) {
  const sd = state.settings;
  const initial = () => ({
    amount: sd.goal ? fmtAmount(sd.goal.amount) : '', months: sd.goal ? String(sd.goal.months) : '',
    meeting: sd.stageMap?.meeting || '', opportunity: sd.stageMap?.opportunity || '',
    rate: sd.contactToMeetingRate ? String(Math.round(sd.contactToMeetingRate * 1000) / 10).replace('.', ',') : '',
    notes: Object.fromEntries(SD_MODULES.filter(m => sd.modules.includes(m.key)).map(m => [m.dimension, sd.notes[m.dimension] || ''])),
  });
  const [form, setForm] = useState(initial);
  const [error, setError] = useState('');
  const saved = JSON.stringify(initial());
  const dirty = JSON.stringify(form) !== saved;
  useEffect(() => { onDirty(dirty); }, [dirty]);
  useEffect(() => () => onDirty(false), []);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const worked = SD_MODULES.filter(m => sd.modules.includes(m.key));
  const stages = state.crm.stageOrder;

  const save = () => run(async () => {
    setError('');
    const amount = parseAmountEs(form.amount);
    const months = form.months === '' ? null : Number(form.months);
    const rate = form.rate === '' ? null : Number(String(form.rate).replace('%', '').replace(',', '.'));
    if (Number.isNaN(amount) || (amount !== null && (!months || !Number.isInteger(months)))) { setError('La meta necesita un monto (por ejemplo, 300.000) y un plazo en meses entero.'); return; }
    if (rate !== null && !(rate > 0 && rate <= 100)) { setError('La conversión de contacto a reunión es un porcentaje entre 0 y 100 (por ejemplo, 1,3).'); return; }
    if (!!form.meeting !== !!form.opportunity) { setError('Elige las dos etapas: la de reunión y la de oportunidad.'); return; }
    try {
      const next = await adminCall({
        action: 'saveSystemDesign', diagnosticId: state.diagnostic.id,
        goal: amount === null ? null : { amount, months },
        stageMap: form.meeting && form.opportunity ? { meeting: form.meeting, opportunity: form.opportunity } : null,
        contactToMeetingRate: rate === null ? null : rate / 100,
        notes: form.notes,
      });
      onSaved(next);
      const s2 = next.settings;
      setForm({
        amount: s2.goal ? fmtAmount(s2.goal.amount) : '', months: s2.goal ? String(s2.goal.months) : '',
        meeting: s2.stageMap?.meeting || '', opportunity: s2.stageMap?.opportunity || '',
        rate: s2.contactToMeetingRate ? String(Math.round(s2.contactToMeetingRate * 1000) / 10).replace('.', ',') : '',
        notes: Object.fromEntries(worked.map(m => [m.dimension, s2.notes[m.dimension] || ''])),
      });
    } catch (e) { setError(e.message); }
  });

  return (
    <div style={{ ...cardSt, border: dirty ? '1px solid rgba(245,158,11,0.6)' : cardSt.border }}>
      <div style={{ ...labelSt, marginBottom: 6 }}>Datos para calcular</div>
      <div style={{ ...mutedSt, marginBottom: 10 }}>El código calcula los números con esto y con el CRM; lo que falte, el borrador lo marca como "Falta: preguntar en el taller".</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
        <label><span style={labelSt}>Meta (monto, moneda del CRM)</span><input style={inputSt} inputMode="decimal" placeholder="Ej.: 300.000" value={form.amount} onChange={e => set('amount', e.target.value)} /></label>
        <label><span style={labelSt}>Plazo de la meta (meses)</span><input style={inputSt} inputMode="numeric" value={form.months} onChange={e => set('months', e.target.value.replace(/[^0-9]/g, ''))} /></label>
        <label><span style={labelSt}>Etapa del CRM que es "reunión"</span>
          <select style={inputSt} value={form.meeting} onChange={e => set('meeting', e.target.value)} disabled={!stages.length}>
            <option value="">{stages.length ? '—' : 'Sin etapas (importa el CRM)'}</option>{stages.map(x => <option key={x} value={x}>{x}</option>)}
          </select></label>
        <label><span style={labelSt}>Etapa del CRM que es "oportunidad"</span>
          <select style={inputSt} value={form.opportunity} onChange={e => set('opportunity', e.target.value)} disabled={!stages.length}>
            <option value="">—</option>{stages.map(x => <option key={x} value={x}>{x}</option>)}
          </select></label>
        <label><span style={labelSt}>Contactos que terminan en reunión (%)</span><input style={inputSt} inputMode="decimal" value={form.rate} placeholder="Ej.: 1,3 (del intake: 300 contactos → 4 reuniones)" onChange={e => set('rate', e.target.value)} /></label>
      </div>
      <div style={{ ...labelSt, marginTop: 14 }}>Notas de Ale por dimensión</div>
      <div style={{ ...mutedSt, marginBottom: 6 }}>Lo que pasó en los talleres y con el equipo. Entran en el módulo y son evidencia de la medición final (por ejemplo: "la reunión semanal ya se hizo dos veces con la agenda nueva"). Un caso real para los mensajes y cadencias va acá, empezando por "Caso:".</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 10 }}>
        {worked.map(m => (
          <label key={m.key}><span style={{ ...mutedSt, display: 'block', marginBottom: 2 }}>{dimLabel(m.dimension)}</span>
            <textarea style={{ ...textareaSt, minHeight: 70 }} maxLength={SD_NOTE_MAX} value={form.notes[m.dimension] || ''} onChange={e => set('notes', { ...form.notes, [m.dimension]: e.target.value })} /></label>
        ))}
      </div>
      {error && <div style={{ fontSize: 13, color: '#ef4444', marginTop: 10 }}>⚠️ {error}</div>}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'flex-end', marginTop: 10, flexWrap: 'wrap' }}>
        <span style={{ ...mutedSt, marginRight: 'auto' }}>
          {dirty ? <b style={{ color: '#f59e0b' }}>Hay cambios sin guardar: los módulos no se generan hasta guardarlos.</b>
            : `Guardado: ${sd.goal ? `meta ${fmtAmount(sd.goal.amount)} en ${sd.goal.months} ${sd.goal.months === 1 ? 'mes' : 'meses'}` : 'sin meta'} · ${sd.stageMap ? `${sd.stageMap.meeting} → ${sd.stageMap.opportunity}` : 'sin etapas'} · ${sd.contactToMeetingRate ? `contacto a reunión ${String(Math.round(sd.contactToMeetingRate * 1000) / 10).replace('.', ',')} %` : 'sin conversión de contacto a reunión'}`}
        </span>
        {dirty && <button className="action-btn" disabled={busy} onClick={() => { setForm(initial()); setError(''); }}>Descartar</button>}
        <button className="action-btn btn-primary" disabled={busy || !dirty} onClick={save}>💾 Guardar datos</button>
      </div>
    </div>
  );
}

function ModuleRow({ m, busy, titles, selected, blocked, onRun, onOpen }) {
  const def = SD_MODULES.find(x => x.key === m.key);
  const cur = m.current;
  const warnings = checkWarnings(cur?.checks).length;
  const running = cur?.status === 'running';
  return (
    <div style={{ borderBottom: '1px solid var(--globant-border)', padding: '10px 0', ...(selected ? { background: 'rgba(91,191,181,0.06)' } : {}) }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <b style={{ fontSize: 13 }}>{def.number}. {def.label}</b><span style={mutedSt}>{def.title}</span>
        {cur ? <StatusBadge status={cur.status} /> : <span style={badgeSt(false)}>Sin generar</span>}
        {cur && <span style={mutedSt}>v{cur.version}{cur.editedBy ? ' · editada' : ''}</span>}
        {warnings > 0 && cur.status === 'draft' && <span style={{ ...badgeSt(false), color: '#f59e0b' }}>⚠️ {warnings}</span>}
        {cur?.stale && <span style={{ ...badgeSt(false), color: '#f59e0b' }}>Desactualizado</span>}
        <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          {cur && !running && <button className="action-btn" style={{ fontSize: 11, padding: '3px 8px' }} onClick={() => onOpen(cur.id)}>Abrir</button>}
          <button className="action-btn" style={{ fontSize: 11, padding: '3px 8px' }} disabled={busy || running || !!m.gate || blocked}
            title={blocked ? 'Guarda los datos para calcular antes de generar' : undefined}
            onClick={() => (!cur || window.confirm('Se genera una versión nueva y la actual queda en el historial. ¿Seguimos?')) && onRun()}>
            {running ? '⏳ Generando…' : cur ? '↻ Volver a generar' : '✨ Generar'}
          </button>
        </span>
      </div>
      {m.gate && m.gate !== 'running' && <div style={{ ...mutedSt, fontSize: 11, marginTop: 4 }}>{GATES[m.gate] || m.gate}</div>}
      {m.missing?.length > 0 && <div style={{ ...mutedSt, fontSize: 11, marginTop: 4 }}>Va a faltar: {m.missing.join(' · ')}</div>}
      {m.lastFailed && <div style={{ fontSize: 11, color: '#ef4444', marginTop: 4 }}>La última corrida falló ({m.lastFailed.errorKind || 'error'}{m.lastFailed.errorDetail ? `: ${m.lastFailed.errorDetail}` : ''}).</div>}
      {cur && !running && <MethodUsedNote method={cur.methodLibrary} titles={titles} expected />}
    </div>
  );
}

function PlaybookCard({ state, busy, act, open }) {
  const pb = state.playbook;
  const cur = pb.current;
  const approved = state.modules.filter(m => m.selected && m.current?.status === 'approved').length;
  const selected = state.modules.filter(m => m.selected).length;
  return (
    <div style={cardSt}>
      <div style={{ ...labelSt, marginBottom: 6 }}>Playbook</div>
      <div style={{ ...mutedSt, marginBottom: 10 }}>
        Lo arma el código con los módulos aprobados ({approved} de {selected}), sin IA. Se aprueba, se publica con un link y se exporta como .docx para subirlo al Google Drive del cliente.
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        {cur ? <><StatusBadge status={cur.status} /><span style={mutedSt}>v{cur.version}</span></> : <span style={badgeSt(false)}>Sin armar</span>}
        {cur?.published && <span style={badgeSt(true)}>🔗 Publicado{cur.publication.firstOpenedAt ? ` · abierto el ${fmtDate(cur.publication.firstOpenedAt)}` : ''}</span>}
        {cur?.stale && <span style={{ ...badgeSt(false), color: '#f59e0b' }}>Desactualizado: un módulo cambió, ármalo de nuevo</span>}
        <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          {cur && <button className="action-btn" style={{ fontSize: 11, padding: '3px 8px' }} onClick={() => open(cur.id)}>Abrir</button>}
          <button className="action-btn" style={{ fontSize: 11, padding: '3px 8px' }} disabled={busy || !!pb.gate} onClick={() => act({ action: 'sdRun', step: 'sd_playbook' })}>📘 {cur ? 'Volver a armar' : 'Armar playbook'}</button>
        </span>
      </div>
      {pb.gate && <div style={{ ...mutedSt, fontSize: 11, marginTop: 4 }}>{GATES[pb.gate] || pb.gate}</div>}
      {approved < selected && approved > 0 && <div style={{ ...mutedSt, fontSize: 11, marginTop: 4 }}>Los módulos sin aprobar no entran: el playbook dice qué módulos incluye.</div>}
    </div>
  );
}

function ScoreCard({ state, busy, act, open }) {
  const sc = state.score;
  const cur = sc.current;
  const d = state.diagnostic;
  return (
    <div style={cardSt}>
      <div style={{ ...labelSt, marginBottom: 6 }}>Medición final</div>
      <div style={{ ...mutedSt, marginBottom: 10 }}>
        Vuelve a puntuar solo las dimensiones trabajadas, con la misma rúbrica y la misma regla del Diagnostic (evidencia obligatoria). La evidencia son los módulos aprobados y tus notas. La meta es el nivel {SD_TARGET_LEVEL}.
        Rúbrica del diagnóstico: v{d.rubricVersion || '—'} · vigente: v{d.currentRubric}.
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        {cur ? <><StatusBadge status={cur.status} /><span style={mutedSt}>v{cur.version}{cur.rubricVersion ? ` · rúbrica v${cur.rubricVersion}` : ''}</span></> : <span style={badgeSt(false)}>Sin medir</span>}
        {cur?.stale && <span style={{ ...badgeSt(false), color: '#f59e0b' }}>Desactualizada: un módulo o P4 cambió</span>}
        <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          {cur && cur.status !== 'running' && <button className="action-btn" style={{ fontSize: 11, padding: '3px 8px' }} onClick={() => open(cur.id)}>Abrir y validar</button>}
          <button className="action-btn" style={{ fontSize: 11, padding: '3px 8px' }} disabled={busy || !!sc.gate || cur?.status === 'running'} onClick={() => act({ action: 'sdRun', step: 'sd_score' })}>
            {cur?.status === 'running' ? '⏳ Midiendo…' : '📏 Medir'}
          </button>
        </span>
      </div>
      {sc.gate && sc.gate !== 'running' && <div style={{ ...mutedSt, fontSize: 11, marginTop: 4, ...(sc.gate === 'rubric_changed' ? { color: '#f59e0b' } : {}) }}>{GATES[sc.gate] || sc.gate}</div>}
      {sc.detail?.dimensions?.length > 0 && (
        <div style={{ overflowX: 'auto', marginTop: 10 }}>
          <table style={tableSt}>
            <thead><tr>{['Dimensión', 'Antes (Diagnostic)', 'IA propone', 'Después (validado)'].map(h => <th key={h} style={thSt}>{h}</th>)}</tr></thead>
            <tbody>{sc.detail.dimensions.map(x => (
              <tr key={x.key}>
                <td style={tdSt}>{dimLabel(x.key)}</td><td style={tdSt}>{x.before ?? '—'}</td><td style={tdSt}>{x.proposed ?? 'Sin nivel'}</td>
                <td style={tdSt}>{x.validated ? <b>{x.after ?? 'Sin nivel'}</b> : <span style={mutedSt}>sin validar</span>}{x.validated && x.before && x.after ? ` (${x.after > x.before ? '+' : ''}${x.after - x.before})` : ''}</td>
              </tr>
            ))}</tbody>
          </table>
          <div style={{ ...mutedSt, fontSize: 11, marginTop: 4 }}>Rúbrica v{cur?.rubricVersion || d.rubricVersion || '—'}, la misma del Diagnostic.</div>
        </div>
      )}
    </div>
  );
}

function OutputDetail({ output, busy, onAct, open, run, titles, diagnosticId, state, onClose }) {
  const isPlaybook = output.step === 'sd_playbook';
  const isScore = output.step === 'sd_score';
  const def = SD_MODULES.find(m => m.step === output.step);
  const title = isPlaybook ? 'Playbook' : isScore ? 'Medición final' : `${def?.number}. ${def?.label} · ${def?.title}`;
  const editable = !isPlaybook && !isScore && output.isCurrent && output.status === 'draft' && !output.stale;
  const [text, setText] = useState(typeof output.content === 'string' ? output.content : '');
  const changed = editable && text !== (output.content || '');
  const warnings = checkWarnings(output.checks);
  const pub = output.publication || {};

  const approve = () => {
    if (changed) { window.alert('Primero guarda los cambios como versión nueva.'); return; }
    if (warnings.length && !window.confirm(`Esta versión tiene avisos de los controles automáticos:\n\n${warnings.map(w => `• ${w}`).join('\n')}\n\n¿La apruebas igual?`)) return;
    onAct({ action: 'approveDeliverable', outputId: output.id, confirmWarnings: warnings.length > 0 });
  };
  const exportDocx = () => run(async () => { const r = await adminCall({ action: 'exportPlaybookDocx', diagnosticId }); download(r.fileName, r.base64); });
  const preview = () => run(async () => openHtml((await adminCall({ action: 'renderOutput', outputId: output.id })).html));

  return (
    <div style={cardSt}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700 }}>{title} <span style={mutedSt}>v{output.version}</span></div>
          <div style={{ ...mutedSt, marginTop: 4 }}>
            <StatusBadge status={output.status} />{' '}
            {isPlaybook ? `Armado por ${output.editedBy || '—'} el ${fmtDate(output.editedAt || output.created, true)}`
              : output.editedBy ? `Editada por ${output.editedBy} el ${fmtDate(output.editedAt, true)}` : `IA (${output.model || '—'}) · ${fmtDate(output.finishedAt || output.created, true)}`}
            {output.approvedAt && ` · Aprobada por ${output.approvedBy || '—'} el ${fmtDate(output.approvedAt, true)}`}
            {!output.isCurrent && ' · Versión anterior (solo lectura)'}
          </div>
          {output.published && <div style={{ ...mutedSt, marginTop: 4 }}>Publicado por {pub.publishedBy || '—'} el {fmtDate(pub.publishedAt, true)} · {pub.firstOpenedAt ? `abierto por primera vez el ${fmtDate(pub.firstOpenedAt, true)}` : 'todavía no se abrió'}</div>}
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {isPlaybook && <button className="action-btn" style={{ fontSize: 12 }} disabled={busy} onClick={preview}>👁 Vista previa</button>}
          {!isScore && output.isCurrent && output.status === 'draft' && !output.stale && <button className="action-btn btn-primary" style={{ fontSize: 12 }} disabled={busy || changed} onClick={approve}>✓ Aprobar</button>}
          {output.isCurrent && output.status === 'approved' && !output.published && (
            <button className="action-btn" style={{ fontSize: 12 }} disabled={busy}
              onClick={() => window.confirm('Vuelve a borrador para editarlo. ¿Seguimos?') && onAct({ action: 'reopenOutput', outputId: output.id })}>↺ Reabrir</button>
          )}
          {isPlaybook && output.isCurrent && output.status === 'approved' && !output.stale && <>
            {!output.published && <button className="action-btn" style={{ fontSize: 12, background: '#6366f1', color: '#fff', fontWeight: 700 }} disabled={busy}
              onClick={() => window.confirm('Se crea un link al playbook. Cualquiera con el link puede abrirlo: compártelo solo con el cliente. ¿Publicar?') && onAct({ action: 'publishDeliverable', outputId: output.id })}>🚀 Publicar</button>}
            <button className="action-btn" style={{ fontSize: 12 }} disabled={busy} onClick={exportDocx}>⬇️ Descargar .docx</button>
          </>}
          {output.published && <button className="action-btn" style={{ fontSize: 12 }} onClick={() => copy(linkFor(pub.token))}>🔗 Copiar link</button>}
          {output.published && <button className="action-btn" style={{ fontSize: 12, color: '#ef4444' }} disabled={busy}
            onClick={() => window.confirm('El link deja de funcionar ya mismo. ¿Revocar?') && onAct({ action: 'revokeDeliverable', outputId: output.id })}>⛔ Revocar link</button>}
          <button className="action-btn" style={{ fontSize: 12 }} onClick={onClose}>Cerrar</button>
        </div>
      </div>

      {!isPlaybook && !isScore && <MethodLibraryRecord output={output} titles={titles} />}
      {isPlaybook && output.refs?.missing?.length > 0 && <div style={{ ...mutedSt, marginBottom: 8 }}>No incluye (sin aprobar): {output.refs.missing.map(k => SD_MODULES.find(m => m.key === k)?.label || k).join(', ')}.</div>}
      {output.stale && <div style={{ ...warnSt, fontSize: 13 }}>{isPlaybook ? 'Un módulo cambió después de armarlo: vuelve a armar el playbook.' : 'Se generó con una versión anterior de P4 o de los niveles: vuelve a generarlo antes de editarlo o aprobarlo.'}</div>}
      {warnings.length > 0 && (
        <div style={{ ...warnSt, fontSize: 13 }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>⚠️ Avisos de los controles automáticos</div>
          {warnings.map((x, i) => <div key={i}>• {x}</div>)}
        </div>
      )}

      {isScore ? <ScoreValidation output={output} busy={busy} onAct={onAct} state={state} /> : editable ? (
        <>
          <div style={{ ...mutedSt, marginBottom: 6 }}>Edita el texto en Markdown. Las tablas con números las arma el código: al guardar se regeneran desde los datos y un cambio a mano en ellas se descarta. Guardar crea una versión nueva.</div>
          <textarea style={{ ...textareaSt, minHeight: 420, fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12, lineHeight: 1.5 }} value={text} onChange={e => setText(e.target.value)} maxLength={100000} />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
            {changed && <button className="action-btn" style={{ fontSize: 12 }} disabled={busy} onClick={() => setText(output.content || '')}>Descartar cambios</button>}
            <button className="action-btn btn-primary" style={{ fontSize: 12 }} disabled={busy || !changed || !text.trim()} onClick={() => onAct({ action: 'saveEdit', outputId: output.id, baseVersion: output.version, content: text })}>💾 Guardar versión nueva</button>
          </div>
        </>
      ) : (
        <div style={{ whiteSpace: 'pre-wrap', fontSize: 13, lineHeight: 1.6, background: 'rgba(255,255,255,0.03)', borderRadius: 8, padding: 12, maxHeight: 600, overflowY: 'auto' }}>{output.content}</div>
      )}

      {output.history?.length > 1 && (
        <div style={{ marginTop: 14 }}>
          <div style={{ ...labelSt, marginBottom: 6 }}>Historial</div>
          {output.history.map(h => (
            <div key={h.id} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12, padding: '4px 0', borderBottom: '1px solid var(--globant-border)', flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 600 }}>v{h.version}</span><StatusBadge status={h.status} />
              <span style={mutedSt}>{h.editedBy ? `${isPlaybook ? 'Armado' : 'Editada'} por ${h.editedBy} · ${fmtDate(h.editedAt, true)}` : `IA${h.model ? ` (${h.model})` : ''} · ${fmtDate(h.created, true)}`}</span>
              {h.id !== output.id && <button className="action-btn" style={{ fontSize: 11, padding: '2px 8px' }} disabled={busy} onClick={() => open(h.id)}>Ver</button>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** The final measurement: per worked dimension, the level before (P4), the AI proposal with its evidence, and Ale's level. */
function ScoreValidation({ output, busy, onAct, state }) {
  const editable = output.status === 'draft' && output.isCurrent && !output.stale;
  const proposals = Object.fromEntries((output.content?.dimensions || []).map(x => [x.dimension, x]));
  const dims = output.refs?.dimensions || [];
  const saved = output.validated?.dimensions || {};
  const [rows, setRows] = useState(() => Object.fromEntries(dims.map(k => [k, saved[k]
    ? { level: saved[k].level, reason: saved[k].reason || '', validated: !!saved[k].validated }
    : { level: proposals[k]?.proposed_level ?? null, reason: '', validated: false }])));
  const set = (k, patch) => setRows(r => ({ ...r, [k]: { ...r[k], ...patch } }));
  const send = (approve) => onAct({ action: 'validateScore', outputId: output.id, dimensions: rows, approve });
  return (
    <div>
      <div style={{ ...mutedSt, marginBottom: 6 }}>Rúbrica v{output.rubricVersion || '—'} (la del Diagnostic: v{state.diagnostic.rubricVersion || '—'}). Un nivel se alcanza solo si se cumple completo; sin evidencia no hay nivel.</div>
      <div style={{ overflowX: 'auto' }}>
        <table style={tableSt}>
          <thead><tr>{['Dimensión', 'Antes', 'IA propone', 'Evidencia', 'Nivel final', 'Porqué', 'Validada'].map(h => <th key={h} style={thSt}>{h}</th>)}</tr></thead>
          <tbody>{dims.map(k => {
            const p = proposals[k] || {};
            const row = rows[k];
            const changed = row.level !== (p.proposed_level ?? null);
            return (
              <tr key={k}>
                <td style={tdSt}>{dimLabel(k)}</td>
                <td style={tdSt}>{state.levels?.[k] ?? '—'}</td>
                <td style={tdSt}>{p.proposed_level ?? 'Sin nivel'}</td>
                <td style={{ ...tdSt, maxWidth: 360 }}>{(p.evidence || []).map((e, i) => <div key={i} style={{ fontSize: 12 }}>• {e.source === 'ale_notes' ? 'Notas de Ale' : 'Módulo'}: {e.text}</div>)}</td>
                <td style={tdSt}>
                  <select style={{ ...inputSt, padding: '4px 6px', fontSize: 12 }} disabled={!editable} value={row.level ?? ''} onChange={e => set(k, { level: e.target.value === '' ? null : Number(e.target.value) })}>
                    <option value="">Sin nivel</option>{[1, 2, 3, 4].map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </td>
                <td style={tdSt}><textarea style={{ ...textareaSt, minHeight: 40, fontSize: 12 }} disabled={!editable} placeholder={changed ? 'Obligatorio: cambiaste el nivel' : ''} maxLength={2000} value={row.reason} onChange={e => set(k, { reason: e.target.value })} /></td>
                <td style={tdSt}><input type="checkbox" disabled={!editable} checked={row.validated} onChange={e => set(k, { validated: e.target.checked })} /></td>
              </tr>
            );
          })}</tbody>
        </table>
      </div>
      {editable && (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
          <button className="action-btn" disabled={busy} onClick={() => send(false)}>💾 Guardar</button>
          <button className="action-btn btn-primary" disabled={busy} onClick={() => send(true)}>✓ Aprobar la medición</button>
        </div>
      )}
    </div>
  );
}
